import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { dateOnly, timeInTz } from '../../lib/format';
import { PageRoot } from '../ui/Page';
import { UnderlineTabPanel, UnderlineTabStrip, UnderlineTabs } from '../ui/Tabs';
import { JOB_TABS, useUrlTab } from '../ui/pageTabs';
import { BackCrumb, DetailHeader, MetaStrip, SectionCard } from '../ui/Detail';
import { CustomerLinkRow } from '../ui/CustomerLinkRow';
import { StatusBadge, Notice } from '../ui/Badge';
import { Button, ghostButton } from '../ui/Button';
import { JobActions } from '../jobs/JobActions';
import { AddVisitSheet } from '../jobs/AddVisitSheet';
import { BillJobSheet } from '../money/BillJobSheet';
import { BillPartsSheet } from '../money/BillPartsSheet';
import { NotesCard } from '../notes/NotesCard';
import { dash } from '@/components/ui/data-table';
import { EmptyState } from '../ui/EmptyState';
import { ErrorState, GoneState, isNotFound } from '../ui/QueryStates';
import { PageSkeleton } from '../ui/Skeleton';
import { JOB_STATUS, VISIT_STATUS, display } from '../status';
import { JobMoneyPanel } from '../costing/JobMoneyPanel';
import { offersBill } from '../costing/PartsBillingCard';
import { PartsUsedCard } from '../inventory/PartsUsedCard';
import { JobPartsCard } from '../jobs/JobPartsCard';
import { WorkOrderTasksCard } from '../jobs/WorkOrderTasksCard';
import { JobEquipmentCard } from '../equipment/JobEquipmentCard';
import { PhotoGrid } from '../photos/PhotoGrid';
import { TicketsOnJobCard } from '../tickets/TicketsOnJobCard';
import { CustomFieldsCard } from '../customfields/CustomFieldsCard';
import { ProjectLinkCard } from '../projects/ProjectLinkCard';
import { JobChecklistCard } from '../checklists/JobChecklistCard';
import { IconJobs } from '../icons';
import { WithOrgClock } from '../ui/WithOrgClock';
import { EditJobSheet } from '../jobs/EditJobSheet';
import { can, jobMachine, localDateOf, partsClosedReason } from '@servwell/shared';
import { useSession } from '../session';
import { ServiceTypeStep } from '../servicetypes/ServiceTypeStep';
import { ChangePricingCard, JobPricingValue } from '../pricing/JobPricing';
import {
  EXISTING_JOB_PICKER,
  JobServiceTypeValue,
  SetServiceTypeResult,
  useSetServiceType,
} from '../servicetypes/SetServiceType';

// Keys typed to the route's own list (pageTabs) — a label here cannot drift from what
// `/work-orders/$workOrderId`'s validateSearch admits.
const TABS: { key: (typeof JOB_TABS)[number]; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'work', label: 'Work' },
  { key: 'money', label: 'Money' },
  { key: 'notes', label: 'Notes' },
];

/** Execution detail over the legacy job API; workOrderId is the same execution UUID. */
export function WorkOrderDetailPage() {
  return (
    <WithOrgClock what="this work order" icon={IconJobs} title="Work order">
      {(tz) => <JobDetail tz={tz} />}
    </WithOrgClock>
  );
}

function JobDetail({ tz }: { tz: string }) {
  const { workOrderId: jobId } = useParams({ from: '/office/work-orders/$workOrderId' });
  const [tab, setTab] = useUrlTab(JOB_TABS);
  const [addingVisit, setAddingVisit] = useState(false);
  const [billing, setBilling] = useState(false);
  const [editing, setEditing] = useState(false);
  const trpc = useTRPC();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const me = useSession();
  const q = useQuery(trpc.job.get.queryOptions({ jobId }));
  // What a T&M job has left to bill (the money tab's card reads the same): Bill job is offered as
  // the card offers it (`offersBill`), since its invoice bills nothing typed (08).
  const tmParts = useQuery({
    ...trpc.partsBilling.forJob.queryOptions({ jobId }),
    enabled: q.data?.pricing.basis === 'tm',
  });
  const reportLink = useQuery(trpc.job.reportLink.queryOptions({ jobId }));

  /**
   * Changing the job's service type (ops insights 08): the Jobs list's "Set service type", on
   * this one job, for owner and office at any status (`setJobServiceType` says why). Not behind
   * the settled-job test the Edit button sits behind.
   */
  const canSetType = can(me.role, 'job', 'update');
  const [changingType, setChangingType] = useState(false);
  /** The "Change" button: the step hands focus back to it when it closes. */
  const changeTypeButton = useRef<HTMLButtonElement>(null);
  /** Changing this job's pricing basis (Inventory v2 Phase 1): the same gate, the same pattern. */
  const [changingPricing, setChangingPricing] = useState(false);
  const changePricingButton = useRef<HTMLButtonElement>(null);
  const closePricing = () => {
    setChangingPricing(false);
    changePricingButton.current?.focus();
  };
  const setType = useSetServiceType({
    onSuccess: () => {
      // The step goes with the change; focus goes back to the button that opened it, and the
      // answer is announced from the region below.
      setChangingType(false);
      changeTypeButton.current?.focus();
    },
  });

  // Billing lands on the DRAFT invoice rather than silently issuing it — issuing is the
  // moment a number is assigned and the amounts freeze, so it stays a separate decision.
  const cancelVisit = useMutation(
    trpc.visit.cancel.mutationOptions({ onSuccess: () => void qc.invalidateQueries() }),
  );

  const billJob = useMutation(
    trpc.invoice.fromJob.mutationOptions({
      onSuccess: (invoice) => {
        void qc.invalidateQueries();
        void navigate({ to: '/invoices/$invoiceId', params: { invoiceId: invoice.id } });
      },
    }),
  );

  if (q.isPending) return <PageSkeleton />;
  if (q.isError) {
    return (
      <PageRoot>
        <BackCrumb to="/work-orders" label="Work orders" />
        {/* Deleted, or an old link — gone is not broken, and there is nothing to retry. */}
        {isNotFound(q.error) ? (
          <GoneState
            icon={IconJobs}
            what="work order"
            backTo="/jobs"
            backLabel="Back to jobs"
          />
        ) : (
          <ErrorState
            icon={IconJobs}
            what="this work order"
            onRetry={() => void q.refetch()}
          />
        )}
      </PageRoot>
    );
  }
  if (!q.data) {
    return (
      <PageRoot>
        <BackCrumb to="/work-orders" label="Work orders" />
        <EmptyState
          icon={IconJobs}
          title="Work order not found"
          description="It may have been deleted, or the link is stale."
        />
      </PageRoot>
    );
  }

  const { job, customer, property, visits, invoice, checklists, serviceType, pricing } =
    q.data;
  const s = display(JOB_STATUS, job.status);
  const address =
    [property.address1, property.city, property.state, property.zip]
      .filter(Boolean)
      .join(', ') || null;

  return (
    <PageRoot>
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2">
        <BackCrumb to="/work-orders" label="Work orders" />
        {job.commercialJobId ? (
          <Link
            to="/jobs/$jobId"
            params={{ jobId: job.commercialJobId }}
            className="mb-3 inline-block text-sm font-medium text-primary hover:underline"
          >
            Open parent Job
          </Link>
        ) : null}
      </div>
      <DetailHeader
        title={job.title}
        badge={<StatusBadge tone={s.tone}>{s.label}</StatusBadge>}
        right={
          <>
            {/* Once a job is settled the server refuses the edit, so the button goes rather
                than sitting there to produce an error. Same list, same source. */}
            {can(me.role, 'job', 'update') &&
            !([...jobMachine.terminal, 'invoiced'] as string[]).includes(job.status) ? (
              <Button variant="ghost" onClick={() => setEditing(true)}>
                Edit
              </Button>
            ) : null}
            {invoice ? (
              <Link
                to="/invoices/$invoiceId"
                params={{ invoiceId: invoice.id }}
                className={ghostButton}
              >
                {invoice.invoiceNumber
                  ? `Invoice #${invoice.invoiceNumber}`
                  : 'Draft invoice'}
              </Link>
            ) : null}
            {/* A T&M job bills as often as it needs (ticket 12a): Bill job stays, beside its
                invoice, while parts, charges or hours are left to bill. Any other job bills once. */}
            {job.status !== 'canceled' &&
            job.status !== 'closed' &&
            (pricing.basis === 'tm'
              ? tmParts.data !== undefined &&
                offersBill(tmParts.data, can(me.role, 'invoice', 'create'))
              : !invoice) ? (
              <Button
                // A job that came from a quote bills in ONE click — the quote's lines are
                // the sale, and making someone retype them would be a step backwards. Only
                // a job with nothing to copy needs the line builder. Keyed on the server's
                // pricing reason: `job.source` is never 'quote' (every booked job is 'manual').
                onClick={() =>
                  pricing.reason === 'quote'
                    ? billJob.mutate({ jobId, ...idem() })
                    : setBilling(true)
                }
                disabled={billJob.isPending}
              >
                {billJob.isPending
                  ? 'Creating…'
                  : pricing.basis === 'tm'
                    ? 'Bill work order'
                    : 'Invoice this work order'}
              </Button>
            ) : null}
          </>
        }
      />
      {billJob.error ? <Notice tone="danger">{billJob.error.message}</Notice> : null}
      {/* The job's own lifecycle — work done, closed, held, cancelled. Derived from the
          shared state machine so the buttons can never offer an illegal step. */}
      <JobActions jobId={jobId} status={job.status} />
      <MetaStrip
        items={[
          { label: 'Work order', value: `#${job.jobNumber}` },
          { label: 'Customer', value: customer.displayName },
          { label: 'Property', value: property.label },
          // A job from before service types existed has none, and says so with the dash
          // rather than a guess. Owner and office can change it here at any status.
          {
            label: 'Service type',
            value: (
              <JobServiceTypeValue
                label={serviceType?.label ?? null}
                canChange={canSetType}
                open={changingType}
                pending={setType.isPending}
                buttonRef={changeTypeButton}
                onToggle={() => {
                  // Opening asks afresh: nothing picked on an earlier try, no answer left.
                  setType.restart();
                  setChangingType((v) => !v);
                }}
              />
            ),
          },
          // How the job bills its parts, and why (Inventory v2 Phase 1). A quoted job and an
          // agreement visit say why they are Fixed instead of offering a change.
          {
            label: 'Pricing',
            value: (
              <JobPricingValue
                pricing={pricing}
                typeLabel={serviceType?.label ?? null}
                canChange={canSetType}
                open={changingPricing}
                buttonRef={changePricingButton}
                onToggle={() => setChangingPricing((v) => !v)}
              />
            ),
          },
          { label: 'Priority', value: job.priority },
        ]}
      />
      {changingPricing && canSetType && !pricing.locked ? (
        <ChangePricingCard jobId={jobId} current={pricing.basis} onClose={closePricing} />
      ) : null}
      {changingType && canSetType ? (
        <SectionCard title="Change the service type">
          <p className="mb-3 text-sm text-muted-foreground">
            What kind of work is this? The type is internal: it is on no customer
            document, so it can be changed at any status. The change goes into the
            job&apos;s history.
          </p>
          <div className="space-y-3">
            {/* The refusal, in the card it answers, and announced (`alert`). */}
            {setType.error ? (
              <Notice tone="danger" alert>
                {setType.error.message}
              </Notice>
            ) : null}
            <ServiceTypeStep
              value={setType.typeId}
              onChange={setType.pick}
              pending={setType.isPending}
              label="Save"
              pendingLabel="Saving…"
              picker={EXISTING_JOB_PICKER}
              cancelLabel="Cancel"
              onSubmit={(serviceTypeId) => setType.send([job], serviceTypeId)}
              onCancel={() => {
                // Cancel goes away with the card, so focus goes back to "Change".
                setChangingType(false);
                changeTypeButton.current?.focus();
              }}
            />
          </div>
        </SectionCard>
      ) : null}
      {/* Always there for those who can change the type, and filled when a change lands, a
          clean one included (the step has closed by then): announced where focus is. */}
      {canSetType ? (
        <SetServiceTypeResult result={setType.data} sent={setType.sent} />
      ) : null}

      {/* 105: the scroll went into tabs. The header zone above — actions, meta, the
          invoice button — stays visible on every tab; the four panels split what used to
          be one 1,900px column. Content moved verbatim, nothing rewritten. */}
      <UnderlineTabs value={tab} onValueChange={setTab}>
        <UnderlineTabStrip tabs={TABS} />

        <UnderlineTabPanel keepMounted value="overview">
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="space-y-4 lg:col-span-2">
              <SectionCard
                title={`Visits (${visits.length})`}
                right={
                  job.status === 'canceled' || job.status === 'closed' ? null : (
                    <Button variant="ghost" onClick={() => setAddingVisit(true)}>
                      Schedule a visit
                    </Button>
                  )
                }
              >
                {visits.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No visits scheduled yet — schedule one to put this job on the board.
                  </p>
                ) : (
                  <ul className="divide-y divide-border text-sm">
                    {visits.map((v) => {
                      const vs = display(VISIT_STATUS, v.status);
                      return (
                        <li
                          key={v.id}
                          className="flex items-center gap-3 py-2 first:pt-0 last:pb-0"
                        >
                          <span className="w-32 shrink-0 text-xs text-muted-foreground">
                            {v.scheduledStartAt ? (
                              <>
                                {dateOnly(localDateOf(new Date(v.scheduledStartAt), tz))}{' '}
                                <span className="font-mono">
                                  {timeInTz(v.scheduledStartAt, tz)}
                                </span>
                              </>
                            ) : (
                              'Not scheduled'
                            )}
                          </span>
                          <span className="min-w-0 flex-1 truncate">
                            {v.assignees.map((a) => a.name).join(', ') || (
                              <span className="text-muted-foreground">Unassigned</span>
                            )}
                          </span>
                          <StatusBadge tone={vs.tone}>{vs.label}</StatusBadge>
                          {/* Customers cancel. A visit that can't be taken off the board
                          leaves a tech driving to a job that isn't happening. Only the
                          two states the machine allows — never a completed one. */}
                          {v.status === 'scheduled' || v.status === 'en_route' ? (
                            <Button
                              variant="ghost"
                              disabled={cancelVisit.isPending}
                              onClick={() =>
                                cancelVisit.mutate({ visitId: v.id, ...idem() })
                              }
                            >
                              Cancel
                            </Button>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </SectionCard>

              <SectionCard title="Description">
                <p className="whitespace-pre-wrap text-sm">
                  {job.description ?? (
                    <span className="text-muted-foreground">No description.</span>
                  )}
                </p>
              </SectionCard>

              {/* Problems after a visit on this job (ops insights 5a), and raising one. */}
              <TicketsOnJobCard jobId={jobId} customerId={customer.id} tz={tz} />
            </div>

            <div className="space-y-4">
              <SectionCard title="Where">
                <p className="text-sm font-medium">{property.label}</p>
                <p className="text-sm text-muted-foreground">{dash(address)}</p>
              </SectionCard>
              <CustomFieldsCard entityType="job" entityId={jobId} />
              <ProjectLinkCard
                entityType="job"
                entityId={jobId}
                customerId={customer.id}
                projectId={job.projectId}
              />
              <SectionCard title="Who">
                <p className="text-sm font-medium">{customer.displayName}</p>
                <p className="text-sm text-muted-foreground">{dash(customer.phone)}</p>
                <p className="text-sm text-muted-foreground">{dash(customer.email)}</p>
              </SectionCard>
              {/*
                The customer's progress page (118), mounted exactly like the quote and
                invoice links it copies — same component, same "mint on a deliberate press"
                rule. A job becomes addressable from the internet only because somebody
                decided it should.
              */}
              <SectionCard title="Progress page">
                <CustomerLinkRow
                  kind="report"
                  id={jobId}
                  url={reportLink.data?.url ?? null}
                />
                <p className="mt-2 text-xs text-muted-foreground">
                  Shows progress, visits, photos and materials — never money. The customer
                  can sign off here when the work is done.
                </p>
              </SectionCard>
            </div>
          </div>
        </UnderlineTabPanel>

        <UnderlineTabPanel keepMounted value="work">
          <div className="space-y-4">
            <JobChecklistCard jobId={jobId} checklists={checklists} visits={visits} />

            {/* What we worked ON comes before what we put IN it. */}
            <JobEquipmentCard jobId={jobId} propertyId={property.id} />

            {/* The plan, then what was actually used (ops insights 6a): beside the parts-used
                card, not inside it. A plan reserves and bills nothing. */}
            {job.commercialJobId ? (
              <WorkOrderTasksCard
                workOrderId={jobId}
                visits={visits}
                timezone={tz}
                legacyClosedReason={partsClosedReason(job)}
              />
            ) : (
              <JobPartsCard jobId={jobId} closedReason={partsClosedReason(job)} />
            )}

            {/* Parts before photos: they're the half of the margin that used to be a
                number somebody typed in from memory. */}
            <PartsUsedCard
              jobId={jobId}
              // The office's job page is on no visit: what it records reads "Visit not recorded".
              visitId={null}
              showCost
              closedReason={partsClosedReason(job)}
            />

            {/* Every photo from every visit, gathered — the tech attached each to their
                own stop, but the office is asking about the job. */}
            <PhotoGrid entityType="job" entityId={jobId} jobId={jobId} />
          </div>
        </UnderlineTabPanel>

        <UnderlineTabPanel keepMounted value="money">
          {/* The money question — the reason this page exists for an owner. */}
          <JobMoneyPanel jobId={jobId} />
        </UnderlineTabPanel>

        <UnderlineTabPanel keepMounted value="notes">
          <NotesCard entityType="job" entityId={jobId} />
        </UnderlineTabPanel>
      </UnderlineTabs>
      <AddVisitSheet
        jobId={jobId}
        open={addingVisit}
        onClose={() => setAddingVisit(false)}
      />
      {/* A T&M job bills part by part (ticket 12a); any other job types its lines. */}
      {pricing.basis === 'tm' ? (
        billing ? (
          <BillPartsSheet jobId={jobId} onClose={() => setBilling(false)} />
        ) : null
      ) : (
        <BillJobSheet jobId={jobId} open={billing} onClose={() => setBilling(false)} />
      )}
      {editing ? (
        <EditJobSheet key={job.id} open onClose={() => setEditing(false)} job={job} />
      ) : null}
    </PageRoot>
  );
}
